import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import type { ManualOverheadItem } from "../lib/overhead-review.js";

export interface ManualOverheadDraft {
  id?: string;
  name: string;
  kind: "recurring" | "one_time";
  amount: number;
  currency: string;
  effectiveMonth: string;
  endMonth: string | null;
  notes: string | null;
}

interface Props {
  items: ManualOverheadItem[];
  currency: string;
  canEdit: boolean;
  compact: boolean;
  saving: boolean;
  onSave: (draft: ManualOverheadDraft) => Promise<boolean>;
  onRemove: (id: string) => Promise<boolean>;
}

export function ManualOverheadEditor({
  items,
  currency,
  canEdit,
  compact,
  saving,
  onSave,
  onRemove,
}: Props): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [kind, setKind] = useState<"recurring" | "one_time">("recurring");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [effectiveMonth, setEffectiveMonth] = useState(currentMonth());
  const [endMonth, setEndMonth] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!editingId) return;
    const item = items.find((row) => row.id === editingId);
    if (!item) return;
    setKind(item.kind);
    setName(item.name);
    setAmount(String(item.amount));
    setEffectiveMonth(item.effectiveMonth.slice(0, 7));
    setEndMonth(item.endMonth?.slice(0, 7) ?? "");
    setNotes(item.notes ?? "");
    setOpen(true);
  }, [editingId, items]);

  const reset = (): void => {
    setOpen(false);
    setEditingId(null);
    setKind("recurring");
    setName("");
    setAmount("");
    setEffectiveMonth(currentMonth());
    setEndMonth("");
    setNotes("");
  };
  const parsedAmount = Number(amount);
  const valid = name.trim().length > 0
    && Number.isFinite(parsedAmount)
    && parsedAmount >= 0
    && /^\d{4}-\d{2}$/.test(effectiveMonth);

  return (
    <section style={styles.card}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <h2 style={styles.title}>Costs missing from QuickBooks</h2>
          <p style={styles.description}>
            Add a cost that is not in the report, or a new cost that starts soon. Manual items stay separate from QuickBooks.
          </p>
        </div>
        {canEdit && !open && (
          <button type="button" onClick={() => setOpen(true)} style={styles.primaryButton}>
            <Plus size={15} /> Add a cost
          </button>
        )}
      </div>

      {open && (
        <form
          style={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            if (!valid) return;
            void onSave({
              ...(editingId ? { id: editingId } : {}),
              name: name.trim(),
              kind,
              amount: parsedAmount,
              currency,
              effectiveMonth: `${effectiveMonth}-01`,
              endMonth: kind === "recurring" && endMonth ? `${endMonth}-01` : null,
              notes: notes.trim() || null,
            }).then((saved) => { if (saved) reset(); });
          }}
        >
          <div style={styles.toggle}>
            <button type="button" onClick={() => setKind("recurring")} style={styles.toggleButton(kind === "recurring")}>Regular monthly cost</button>
            <button type="button" onClick={() => setKind("one_time")} style={styles.toggleButton(kind === "one_time")}>One-time cost</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "minmax(220px, 1.4fr) minmax(130px, .65fr) minmax(150px, .75fr) minmax(150px, .75fr)", gap: 10 }}>
            <label style={styles.fieldLabel}>
              Expense name
              <input value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Warehouse security" style={styles.input} />
            </label>
            <label style={styles.fieldLabel}>
              {kind === "recurring" ? "Monthly amount" : "Amount"}
              <div style={{ position: "relative" }}>
                <span aria-hidden="true" style={styles.currencyPrefix}>$</span>
                <input type="number" inputMode="decimal" min="0" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} style={{ ...styles.input, paddingLeft: 22 }} />
              </div>
            </label>
            <label style={styles.fieldLabel}>
              {kind === "recurring" ? "Starts" : "Happened in"}
              <input type="month" value={effectiveMonth} onChange={(event) => setEffectiveMonth(event.target.value)} style={styles.input} />
            </label>
            {kind === "recurring" ? (
              <label style={styles.fieldLabel}>
                Ends (optional)
                <input type="month" value={endMonth} min={effectiveMonth} onChange={(event) => setEndMonth(event.target.value)} style={styles.input} />
              </label>
            ) : <div />}
          </div>

          <label style={styles.fieldLabel}>
            Note (optional)
            <input value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Why QuickBooks does not show this yet" style={styles.input} />
          </label>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" onClick={reset} style={styles.secondaryButton}>Cancel</button>
            <button type="submit" disabled={!valid || saving} style={styles.primaryButton}>
              {saving ? "Saving..." : editingId ? "Save changes" : "Add cost"}
            </button>
          </div>
        </form>
      )}

      {items.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
          {items.map((item) => (
            <div key={item.id} style={styles.itemRow}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>{item.name}</div>
                <div style={{ color: "var(--faint)", fontSize: 11.5, marginTop: 3 }}>
                  {item.kind === "recurring"
                    ? `${money(item.amount, item.currency)} each month starting ${monthLabel(item.effectiveMonth)}${item.endMonth ? ` through ${monthLabel(item.endMonth)}` : ""}`
                    : `${money(item.amount, item.currency)} in ${monthLabel(item.effectiveMonth)}`}
                  {item.notes ? ` | ${item.notes}` : ""}
                </div>
              </div>
              {canEdit && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button type="button" onClick={() => setEditingId(item.id)} style={styles.iconButton} aria-label={`Edit ${item.name}`}>
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => {
                      if (window.confirm(`Remove ${item.name}?`)) void onRemove(item.id);
                    }}
                    style={{ ...styles.iconButton, color: "var(--bad)" }}
                    aria-label={`Remove ${item.name}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : !open ? (
        <div style={styles.empty}>No manual costs added.</div>
      ) : null}
    </section>
  );
}

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function money(value: number, currency: string): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(value);
}

function monthLabel(value: string): string {
  return new Date(`${value.slice(0, 7)}-15T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

const styles = {
  card: {
    padding: 18,
    border: "1px solid var(--border)",
    borderRadius: 10,
    background: "var(--surface)",
  } as React.CSSProperties,
  title: { margin: 0, fontSize: 15.5, fontWeight: 750 } as React.CSSProperties,
  description: { margin: "4px 0 0", color: "var(--body)", fontSize: 12.5, lineHeight: 1.5, maxWidth: 680 } as React.CSSProperties,
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginTop: 16,
    padding: 14,
    border: "1px solid var(--border)",
    borderRadius: 9,
    background: "var(--surface-2)",
  } as React.CSSProperties,
  toggle: { display: "inline-flex", alignSelf: "flex-start", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" } as React.CSSProperties,
  toggleButton: (active: boolean): React.CSSProperties => ({
    border: "none",
    padding: "7px 12px",
    background: active ? "var(--accent)" : "transparent",
    color: active ? "var(--accent-contrast)" : "var(--body)",
    cursor: "pointer",
    font: "inherit",
    fontSize: 12,
    fontWeight: 650,
  }),
  fieldLabel: { display: "flex", flexDirection: "column", gap: 5, color: "var(--faint)", fontSize: 11.5, fontWeight: 650 } as React.CSSProperties,
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 9px",
    background: "var(--surface)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 7,
    font: "inherit",
    fontSize: 12.5,
  } as React.CSSProperties,
  currencyPrefix: { position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "var(--faint)", fontSize: 12, pointerEvents: "none" } as React.CSSProperties,
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    border: "none",
    borderRadius: 7,
    padding: "8px 12px",
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    cursor: "pointer",
    font: "inherit",
    fontSize: 12.5,
    fontWeight: 700,
    whiteSpace: "nowrap",
  } as React.CSSProperties,
  secondaryButton: {
    border: "1px solid var(--border)",
    borderRadius: 7,
    padding: "7px 11px",
    background: "transparent",
    color: "var(--body)",
    cursor: "pointer",
    font: "inherit",
    fontSize: 12.5,
    fontWeight: 650,
  } as React.CSSProperties,
  itemRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "10px 12px",
    background: "var(--surface-2)",
    borderRadius: 8,
  } as React.CSSProperties,
  iconButton: {
    width: 31,
    height: 31,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid var(--border)",
    borderRadius: 7,
    background: "transparent",
    color: "var(--body)",
    cursor: "pointer",
  } as React.CSSProperties,
  empty: { marginTop: 14, padding: "12px 14px", border: "1px dashed var(--border)", borderRadius: 8, color: "var(--faint)", fontSize: 12.5 } as React.CSSProperties,
};
